<template>
  <div
    class="base-uploader-file-card"
    :class="{
      [`base-uploader-file-card--variant-${props.variant}`]: true,
      'base-uploader-file-card--with-remove': props.withRemove,
      'base-uploader-file-card--clickable': props.clickable,
    }"
    @click="handleClick"
  >
    <BaseIcon
      name="vuesax:document-text-linear"
      size="20px"
      color="var(--color-foreground-primary)"
      class="base-uploader-file-card__icon"
    />
    <div class="base-uploader-file-card__text">
      <BaseTypography
        type="text-mini-alt"
        weight="600"
        color="var(--color-foreground-primary)"
        class="base-uploader-file-card__name"
        nowrap
      >
        {{ props.fileName }}
      </BaseTypography>
      <BaseTypography
        type="caption"
        weight="600"
        color="var(--color-foreground-caption)"
        class="base-uploader-file-card__size"
      >
        {{ fileSizeLabel }}
      </BaseTypography>
    </div>
    <BaseButtonIcon
      v-if="props.withRemove"
      class="base-uploader-file-card__remove"
      icon-name="vuesax:close-circle-linear"
      icon-size="12px"
      icon-color="var(--color-foreground-caption)"
      variant="transparent"
      size="xxxs"
      form="circle"
      @click.stop="emit('remove')"
    />
  </div>
</template>

<script setup lang="ts">
import { formatFileSize } from '~/shared/lib'
import BaseButtonIcon from '../button-icon/index.vue'
import BaseIcon from '../icon/index.vue'
import BaseTypography from '../typography/index.vue'

interface Props {
  fileName: string
  fileSize?: number | null
  variant?: 'light' | 'secondary'
  withRemove?: boolean
  clickable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  fileSize: null,
  variant: 'light',
  withRemove: false,
  clickable: false,
})

const emit = defineEmits<{
  remove: []
  click: []
}>()

const fileSizeLabel = computed(() => {
  if (props.fileSize == null) {
    return '—'
  }
  return formatFileSize(props.fileSize)
})

function handleClick() {
  if (!props.clickable) {
    return
  }
  emit('click')
}
</script>

<style lang="scss" scoped>
.base-uploader-file-card {
  position: relative;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: rem(8px);
  min-width: rem(140px);
  max-width: rem(220px);
  padding: rem(10px);
  border-radius: rem(10px);

  &--variant-light {
    background-color: var(--color-surface-bg);
  }

  &--variant-secondary {
    background-color: var(--color-surface-placeholder);
  }

  &--with-remove {
    padding-right: rem(36px);
  }

  &--clickable {
    cursor: pointer;
  }

  &__icon {
    flex-shrink: 0;
  }

  &__text {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0;
    min-width: 0;
  }

  &__name {
    width: 100%;
  }

  &__size {
    font-size: rem(6px);
    line-height: 1;
    font-weight: 600;
  }

  &__remove {
    position: absolute;
    top: rem(6px);
    right: rem(6px);
    flex-shrink: 0;
  }
}
</style>
